import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { debounce } from 'lodash';
import { scrollToElement } from '@/utils/scroll';

interface FaqData {
    question: string;
    answer: string;
}

type FaqDataArray = FaqData[];

@Component
export class FaqContainer extends ThemeComponent {
    @Ref data!: HTMLScriptElement;
    @Ref searchContainer!: HTMLElement;
    @Ref searchInput!: HTMLInputElement;
    @Ref searchResultsContainer!: HTMLElement;
    @Ref faqFilters!: HTMLElement[];

    faqData: FaqDataArray|null = null;

    init() {

        // Init observer
        this.initObserver();

        // Attempt to parse JSON
        this.faqData = this.parseData();
        if (this.faqData === null) {
            this.$log.error('FAQ - unable to access FAQ data');
            return;
        }

        // Listen for search input
        this.handleSearch();

        // Handle close events
        this.handleCloseEvents();

        this.handleFilters();
    }

    initObserver() {
        const observer = new MutationObserver(() => {
            // eslint-disable-next-line max-len
            const resultItems = this.searchResultsContainer.querySelectorAll('.faq-search__results-item:not(.faq-search__results-item--no-results)');
            for (const resultItem of resultItems) {
                const resultItemElement = resultItem as HTMLElement;
                this.$listen(resultItemElement, 'click', () => {
                    this.handleResultClick(resultItemElement);
                });
            }
        });
        observer.observe(this.searchResultsContainer, {
            subtree: true,
            childList: true
        });
    }

    handleResultClick(resultItemElement: HTMLElement) {

        // Remove classes from search
        this.searchContainer.classList.remove('faq-search--results-active');

        // Handleize the selected item title so we can find the correct FAQ item
        let selectedItemContent = resultItemElement.innerHTML.toLowerCase();
        selectedItemContent = selectedItemContent.replace('&amp;', '');
        selectedItemContent = selectedItemContent.replace('&', '');
        selectedItemContent = selectedItemContent.replace(/[^\w\s]/gi, '');
        selectedItemContent = selectedItemContent.replace(/[^a-z0-9]+/g, '-');
        selectedItemContent = selectedItemContent.replace(/-$/, '');
        selectedItemContent = selectedItemContent.replace(/^-/, '');

        // Open the correct FAQ accordion item
        const titleIdentifier = selectedItemContent;
        const matchingFaqItem: HTMLElement|null = this.querySelector(`[data-faq-identifier=${titleIdentifier}]`);
        if (matchingFaqItem === null) {
            return;
        }
        const accordionItem = matchingFaqItem.querySelector('.accordion__body ');
        if (accordionItem && !accordionItem.classList.contains('accordion__header--active')) {
            const accordionHeader: HTMLElement|null = matchingFaqItem.querySelector('.accordion__header');
            if (accordionHeader) {
                accordionHeader.click();
            }
        }

        // Scroll page to the correct FAQ accordion item
        scrollToElement(matchingFaqItem);
    }

    parseData() {
        let data = null;
        if (this.data) {
            try {
                data = JSON.parse(this.data.innerHTML);
            } catch(event) {
                this.$log.error('FAQ parse error', {
                    event
                });
            }
        }
        return data;
    }

    handleSearch() {
        this.$listen(this.searchInput, 'input', debounce((e) => {
            this.searchContainer.classList.add('faq-search--results-active');
            const enteredText = this.searchInput.value;
            const results = this.filterResults(enteredText);
            this.updateResultsMarkup(results);
        }));
    }

    filterResults(enteredText: string) {
        let results: string[] = [];
        if (this.faqData !== null) {
            for (const data of this.faqData) {
                const valueToCheck = enteredText.toLowerCase();
                const questionToCheck = data.question.toLowerCase();
                const answerToCheck = data.answer.toLowerCase();
                if (questionToCheck.includes(valueToCheck) || answerToCheck.includes(valueToCheck)) {
                    results.push(data.question);
                }
            }
        }
        results = results.slice(0, 5);
        return results;
    }

    updateResultsMarkup(results: string[]) {
        let resultsMarkup = '';
        for (const [index, result] of results.entries()) {
            if(index < 5) {
                const resultMarkup = `
                    <div class="faq-search__results-item">
                        ${result}
                    </div>
                `;
                resultsMarkup += resultMarkup;
            } else {
                break;
            }
        }
        if (resultsMarkup === '') {
            resultsMarkup = `
                <div class="faq-search__results-item faq-search__results-item--no-results">
                    No Results Found
                </div>
            `;
        }
        this.searchResultsContainer.innerHTML = resultsMarkup;
    }

    handleCloseEvents() {
        this.$listen(document.body, 'click', (e) => {
            const clickedElement = e.target as HTMLElement;
            if (this.searchContainer === clickedElement || this.searchContainer.contains(clickedElement)) {
                return;
            }
            this.searchContainer.classList.remove('faq-search--results-active');
        });
    }

    handleFilters() {
        for(const filter of this.faqFilters) {
            this.$listen(filter, 'click', () => {
                const goToCategory = filter.dataset.faqFilter;

                if(!goToCategory === undefined) {
                    this.$log.error('[data-faq-filter] attribute is not present on element');
                    return;
                }

                if(goToCategory === '') {
                    this.$log.error('[data-faq-filter] attribute is left blank');
                    return;
                }

                const category = document.getElementById(`${goToCategory}`);

                if(category) {
                    category.scrollIntoView();
                } else {
                    this.$log.error('category does not exist');
                }
            });
        }
    }
}
